import { Box, Typography } from "@mui/material";
import ColumnFilter from "../Common/customFilter";
import EditableCell from "./EditableCell";
import { formatDateOnly } from "../../utils/utils";

export interface MMNAReportRow {
  id?: string;
  opportunityId: string;
  aeTeam: string | null;
  opportunityName: string | null;
  arrAmount: number | null;
  stage: string | null;
  closeDate: string | null;
  managerForecast: string | null;
  marketPodLead: string | null;
  opportunityOwner: string | null;
  l1Ryg: string | null;
  pricingModelOffered: string | null;
  l3TraditionalPricingException: string | null;
  managerForecastRationale: string | null;
  whatIsOurBiggestHurdle: string | null;
  linkToObpCommercialPitchDocument: string | null;
  sellerForecast: string | null;
  buyerMode: string | null;
  opportunityRecordType: string | null;
  accountName: string | null;
  age: string | null;
  stageDuration: string | null;
  annualRevenue: number | null;
  amount: number | null;
  createdDate: string | null;
  indiaSp: string | null;
  spPodLead: string | null;
  digitalTransformationPrincipal: string | null;
  opportunitySource: string | null;
  numberOfStrapsExecuted: number | null;
  erpSystemsOthers: string | null;
  dealType: string | null;
  region: string | null;
  forecastArr: number | null;
  eighteenCharacterAccountId: string | null;
  eighteenCharOppId: string | null;
}

export interface MMNAReportFilterProps {
  filters?: {
    aeTeam?: string[];
    stage?: string[];
    managerForecast?: string[];
    dealType?: string[];
    opportunityRecordType?: string[];
    closeDate?: any;
    createdDate?: any;
    arrAmount?: any;
    opportunityName?: string | string[];
    marketPodLead?: string | string[];
    opportunityOwner?: string | string[];
    accountName?: string | string[];
    indiaSp?: string | string[];
    spPodLead?: string | string[];
    buyerMode?: string[];
    l1Ryg?: string[];
    opportunitySource?: string[];
  };
  onFilterChange?: (columnKey: string, value: any) => void;
  onSearchChange?: (columnKey: string, query: string) => void;
  filterOptions?: {
    aeTeam?: { label: string; value: string }[];
    stage?: { label: string; value: string }[];
    managerForecast?: { label: string; value: string }[];
    dealType?: { label: string; value: string }[];
    opportunityRecordType?: { label: string; value: string }[];
    buyerMode?: { label: string; value: string }[];
    l1Ryg?: { label: string; value: string }[];
    opportunitySource?: { label: string; value: string }[];
    opportunityName?: { label: string; value: string }[];
    marketPodLead?: { label: string; value: string }[];
    opportunityOwner?: { label: string; value: string }[];
    accountName?: { label: string; value: string }[];
    indiaSp?: { label: string; value: string }[];
    spPodLead?: { label: string; value: string }[];
  };
}

const renderMultiFilterHeader = (
  title: string,
  columnKey: string,
  filters: MMNAReportFilterProps["filters"],
  onFilterChange: MMNAReportFilterProps["onFilterChange"],
  options: { label: string; value: string }[] = [],
  openFilterColumn: string | null | undefined,
  setOpenFilterColumn: ((key: string | null) => void) | undefined,
  showSearch = true,
  onSearchChange?: MMNAReportFilterProps["onSearchChange"],
  isLoading: boolean | ((colKey: string) => boolean) = false
) => {
  const currentVal = (filters as any)?.[columnKey] ?? [];
  const isActive = Array.isArray(currentVal) && currentVal.length > 0;
  return (
    <Box className={`mm-na-header-cell ${openFilterColumn === columnKey ? "mm-na-header-cell--active" : ""}`}>
      <Typography sx={{ fontWeight: 600, fontSize: "14px", color: isActive ? "#6036d6" : "inherit" }}>
        {title}
      </Typography>
      {filters && onFilterChange && (
        <Box onClick={(e) => e.stopPropagation()} sx={{ display: "inline-flex" }}>
          <ColumnFilter
            currentValue={currentVal}
            onApply={(val) => {
              if (Array.isArray(val)) onFilterChange(columnKey, val);
              else if (typeof val === "string") onFilterChange(columnKey, val ? [val] : []);
              else onFilterChange(columnKey, []);
            }}
            options={options}
            type="multi"
            columnKey={columnKey}
            showSections={{ sort: false, search: showSearch, options: true, apply: true }}
            isActive={isActive}
            onOpen={() => setOpenFilterColumn?.(columnKey)}
            onClose={() => setOpenFilterColumn?.(null)}
            onSearchChange={onSearchChange ? (query) => onSearchChange(columnKey, query) : undefined}
            isLoading={isLoading}
          />
        </Box>
      )}
    </Box>
  ) as unknown as string;
};

const renderDateFilterHeader = (
  title: string,
  columnKey: string,
  filters: MMNAReportFilterProps["filters"],
  onFilterChange: MMNAReportFilterProps["onFilterChange"],
  openFilterColumn: string | null | undefined,
  setOpenFilterColumn: ((key: string | null) => void) | undefined
) => {
  const dateVal = (filters as any)?.[columnKey];
  const isActive = Boolean(dateVal?.startDate || dateVal?.endDate);
  return (
    <Box className={`mm-na-header-cell ${openFilterColumn === columnKey ? "mm-na-header-cell--active" : ""}`}>
      <Typography sx={{ fontWeight: 600, fontSize: "14px", color: isActive ? "#6036d6" : "inherit" }}>
        {title}
      </Typography>
      {filters && onFilterChange && (
        <Box onClick={(e) => e.stopPropagation()} sx={{ display: "inline-flex" }}>
          <ColumnFilter
            currentValue={dateVal}
            onApply={(val) => onFilterChange(columnKey, val)}
            options={[]}
            type="date"
            columnKey={columnKey}
            showSections={{ sort: false, search: false, options: false, apply: true }}
            isActive={isActive}
            onOpen={() => setOpenFilterColumn?.(columnKey)}
            onClose={() => setOpenFilterColumn?.(null)}
          />
        </Box>
      )}
    </Box>
  ) as unknown as string;
};


export const getReportColumns = (
  onEditClick: (anchorEl: HTMLElement, rowId: string, columnId: keyof MMNAReportRow, columnName: string, val: string) => void,
  editingInfo?: { rowId: string; columnId: keyof MMNAReportRow } | null,
  filters?: MMNAReportFilterProps["filters"],
  onFilterChange?: MMNAReportFilterProps["onFilterChange"],
  filterOptions?: MMNAReportFilterProps["filterOptions"],
  openFilterColumn?: string | null,
  setOpenFilterColumn?: (key: string | null) => void,
  onSearchChange?: MMNAReportFilterProps["onSearchChange"],
  isPresearchLoading?: boolean | ((colKey: string) => boolean)
) => [
  {
    accessorKey: "aeTeam",
    header: "AE Team",
    Header: renderMultiFilterHeader(
      "AE Team",
      "aeTeam",
      filters,
      onFilterChange,
      filterOptions?.aeTeam ?? [],
      openFilterColumn,
      setOpenFilterColumn,
      true
    ),
    minSize: 150,
    enableSorting: true,
  },
  {
    accessorKey: "opportunityName",
    header: "Opportunity Name",
    Header: renderMultiFilterHeader(
      "Opportunity Name",
      "opportunityName",
      filters,
      onFilterChange,
      filterOptions?.opportunityName ?? [],
      openFilterColumn,
      setOpenFilterColumn,
      true,
      onSearchChange,
      isPresearchLoading
    ),
    minSize: 220,
    enableSorting: true,
    Cell: ({ cell }: any) => {
      const val = cell.getValue() as string;
      return <span className="mm-na-cell-text" title={val || undefined}>{val || "-"}</span>;
    },
  },
  {
    accessorKey: "arrAmount",
    header: "ARR Amount",
    Header: (
      <Box className={`mm-na-header-cell ${openFilterColumn === "arrAmount" ? "mm-na-header-cell--active" : ""}`}>
        <Typography sx={{ fontWeight: 600, fontSize: "14px", color: filters?.arrAmount?.value !== undefined ? "#6036d6" : "inherit" }}>
          ARR Amount
        </Typography>
        {filters && onFilterChange && (
          <Box onClick={(e) => e.stopPropagation()} sx={{ display: "inline-flex" }}>
            <ColumnFilter
              currentValue={filters.arrAmount}
              onApply={(val) => onFilterChange("arrAmount", val)}
              options={[]}
              type="number"
              columnKey="arrAmount"
              showSections={{ sort: false, search: false, options: false, apply: true }}
              isActive={filters?.arrAmount?.value !== undefined}
              onOpen={() => setOpenFilterColumn?.("arrAmount")}
              onClose={() => setOpenFilterColumn?.(null)}
            />
          </Box>
        )}
      </Box>
    ) as unknown as string,
    minSize: 120,
    enableSorting: true,
    Cell: ({ cell }: any) => {
      const val = cell.getValue();
      if (val === null || val === undefined || isNaN(Number(val))) return "-";
      return `$${Number(val).toLocaleString("en-US")}`;
    },
  },
  {
    accessorKey: "stage",
    header: "Stage",
    Header: renderMultiFilterHeader(
      "Stage",
      "stage",
      filters,
      onFilterChange,
      filterOptions?.stage ?? [],
      openFilterColumn,
      setOpenFilterColumn,
      true
    ),
    minSize: 150,
    enableSorting: true,
  },
  {
    accessorKey: "closeDate",
    header: "Close Date",
    Header: renderDateFilterHeader(
      "Close Date",
      "closeDate",
      filters,
      onFilterChange,
      openFilterColumn,
      setOpenFilterColumn
    ),
    minSize: 150,
    size: 150,
    enableSorting: true,
    Cell: ({ cell }: any) => formatDateOnly(cell.getValue() as string),
  },
  {
    accessorKey: "managerForecast",
    header: "Manager Forecast",
    Header: renderMultiFilterHeader(
      "Manager Forecast",
      "managerForecast",
      filters,
      onFilterChange,
      filterOptions?.managerForecast ?? [],
      openFilterColumn,
      setOpenFilterColumn,
      false
    ),
    minSize: 180,
    enableSorting: false,
  },
  {
    accessorKey: "marketPodLead",
    header: "Market POD Lead",
    Header: renderMultiFilterHeader(
      "Market POD Lead",
      "marketPodLead",
      filters,
      onFilterChange,
      filterOptions?.marketPodLead ?? [],
      openFilterColumn,
      setOpenFilterColumn,
      true,
      onSearchChange,
      isPresearchLoading
    ),
    minSize: 150,
    enableSorting: false,
  },
  {
    accessorKey: "opportunityOwner",
    header: "Opportunity Owner",
    Header: renderMultiFilterHeader(
      "Opportunity Owner",
      "opportunityOwner",
      filters,
      onFilterChange,
      filterOptions?.opportunityOwner ?? [],
      openFilterColumn,
      setOpenFilterColumn,
      true,
      onSearchChange,
      isPresearchLoading
    ),
    minSize: 200,
    size: 200,
    enableSorting: false,
  },
  {
    accessorKey: "l1Ryg",
    header: "L1 RYG",
    Header: renderMultiFilterHeader(
      "L1 RYG",
      "l1Ryg",
      filters,
      onFilterChange,
      filterOptions?.l1Ryg ?? [],
      openFilterColumn,
      setOpenFilterColumn,
      false
    ),
    minSize: 120,
    size: 120,
    enableSorting: false,
  },
  {
    accessorKey: "pricingModelOffered",
    header: "Pricing Model Offered",
    minSize: 180,
    enableSorting: false,
  },
  {
    accessorKey: "l3TraditionalPricingException",
    header: "L3 Traditional Pricing Exception",
    minSize: 240,
    enableSorting: false,
  },
  {
    accessorKey: "managerForecastRationale",
    header: "Manager Forecast Rationale",
    minSize: 300,
    enableSorting: false,
    Cell: ({ cell, row }: any) => {
      const isEditing = editingInfo?.rowId === row.original.opportunityId && editingInfo?.columnId === "managerForecastRationale";
      return (
        <EditableCell
          value={cell.getValue() as string}
          active={isEditing}
          onClick={(anchorEl) =>
            onEditClick(
              anchorEl,
              row.original.opportunityId,
              "managerForecastRationale",
              "Manager Forecast Rationale",
              cell.getValue() as string
            )
          }      
        />
      );
    },
  },
  {
    accessorKey: "whatIsOurBiggestHurdle",
    header: "What is our biggest hurdle?",
    minSize: 300,
    enableSorting: false,
    Cell: ({ cell, row }: any) => {
      const isEditing = editingInfo?.rowId === row.original.opportunityId && editingInfo?.columnId === "whatIsOurBiggestHurdle";
      return (
        <EditableCell
          value={cell.getValue() as string}
          active={isEditing}
          onClick={(anchorEl) =>
            onEditClick(
              anchorEl,
              row.original.opportunityId,
              "whatIsOurBiggestHurdle",
              "What is our biggest hurdle?",
              cell.getValue() as string
            )
          }
        />
      );
    },
  },
  {
    accessorKey: "linkToObpCommercialPitchDocument",
    header: "Link to OBP Commercial Pitch Document",
    minSize: 300,
    enableSorting: false,
    Cell: ({ cell }: any) => {
      const val = cell.getValue() as string;
      if (!val) return "-";
      const isUrl = val.startsWith("http://") || val.startsWith("https://");
      return (
        <span className="mm-na-cell-text" title={val}>
          {isUrl ? (
            <a
              href={val}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#1976d2", textDecoration: "underline" }}
            >
              {val}
            </a>
          ) : (
            val
          )}
        </span>
      );
    },
  },
  {
    accessorKey: "sellerForecast",
    header: "Seller Forecast",
    minSize: 150,
    enableSorting: false,
  },
  {
    accessorKey: "buyerMode",
    header: "Buyer Mode",
    Header: renderMultiFilterHeader(
      "Buyer Mode",
      "buyerMode",
      filters,
      onFilterChange,
      filterOptions?.buyerMode ?? [],
      openFilterColumn,
      setOpenFilterColumn,
      false
    ),
    minSize: 150,
    enableSorting: false,
  },
  {
    accessorKey: "opportunityRecordType",
    header: "Opportunity Record Type",
    Header: renderMultiFilterHeader(
      "Opportunity Record Type",
      "opportunityRecordType",
      filters,
      onFilterChange,
      filterOptions?.opportunityRecordType ?? [],
      openFilterColumn,
      setOpenFilterColumn,
      false
    ),
    minSize: 250,
    enableSorting: false,
  },
  {
    accessorKey: "accountName",
    header: "Account Name",
    Header: renderMultiFilterHeader(
      "Account Name",
      "accountName",
      filters,
      onFilterChange,
      filterOptions?.accountName ?? [],
      openFilterColumn,
      setOpenFilterColumn,
      true,
      onSearchChange,
      isPresearchLoading
    ),
    minSize: 180,
    enableSorting: false,
  },
  {
    accessorKey: "age",
    header: "Age",
    minSize: 80,
    size: 80,
    enableSorting: false,
  },
  {
    accessorKey: "stageDuration",
    header: "Stage Duration",
    minSize: 130,
    size: 130,
    enableSorting: false,
  },
  {
    accessorKey: "annualRevenue",
    header: "Annual Revenue",
    minSize: 150,
    enableSorting: true,
    Cell: ({ cell }: any) => {
      const val = cell.getValue();
      if (val === null || val === undefined || isNaN(Number(val))) return "-";
      return `$${Number(val).toLocaleString("en-US")}`;
    },
  },
  {
    accessorKey: "amount",
    header: "Amount",
    minSize: 120,
    size: 120,
    enableSorting: true,
    Cell: ({ cell }: any) => {
      const val = cell.getValue();
      if (val === null || val === undefined || isNaN(Number(val))) return "-";
      return `$${Number(val).toLocaleString("en-US")}`;
    },
  },
  {
    accessorKey: "createdDate",
    header: "Created Date",
    Header: renderDateFilterHeader(
      "Created Date",
      "createdDate",
      filters,
      onFilterChange,
      openFilterColumn,
      setOpenFilterColumn
    ),
    minSize: 180,
    size: 180,
    enableSorting: true,
    Cell: ({ cell }: any) => formatDateOnly(cell.getValue() as string),
  },
  {
    accessorKey: "indiaSp",
    header: "India SP",
    Header: renderMultiFilterHeader(
      "India SP",
      "indiaSp",
      filters,
      onFilterChange,
      filterOptions?.indiaSp ?? [],
      openFilterColumn,
      setOpenFilterColumn,
      true,
      onSearchChange,
      isPresearchLoading
    ),
    minSize: 150,
    enableSorting: false,
  },
  {
    accessorKey: "spPodLead",
    header: "SP POD Lead",
    Header: renderMultiFilterHeader(
      "SP POD Lead",
      "spPodLead",
      filters,
      onFilterChange,
      filterOptions?.spPodLead ?? [],
      openFilterColumn,
      setOpenFilterColumn,
      true,
      onSearchChange,
      isPresearchLoading
    ),
    minSize: 150,
    enableSorting: false,
  },
  {
    accessorKey: "digitalTransformationPrincipal",
    header: "Digital Transformation Principal",
    minSize: 240,
    enableSorting: false,
  },
  {
    accessorKey: "opportunitySource",
    header: "Opportunity Source",
    Header: renderMultiFilterHeader(
      "Opportunity Source",
      "opportunitySource",
      filters,
      onFilterChange,
      filterOptions?.opportunitySource ?? [],
      openFilterColumn,
      setOpenFilterColumn,
      false
    ),
    minSize: 200,
    size: 200,
    enableSorting: false,
  },
  {
    accessorKey: "numberOfStrapsExecuted",
    header: "# of STRAPs Executed",
    minSize: 180,
    enableSorting: false,
  },
  {
    accessorKey: "erpSystemsOthers",
    header: "ERP System(s) (Others)",
    minSize: 200,
    enableSorting: false,
  },
  {
    accessorKey: "dealType",
    header: "Deal Type",
    Header: renderMultiFilterHeader(
      "Deal Type",
      "dealType",
      filters,
      onFilterChange,
      filterOptions?.dealType ?? [],
      openFilterColumn,
      setOpenFilterColumn,
      false
    ),
    minSize: 120,
    enableSorting: false,
  },
  {
    accessorKey: "region",
    header: "Region",
    minSize: 100,
    size: 120,
    enableSorting: false,
  },
  {
    accessorKey: "forecastArr",
    header: "Forecast ARR",
    minSize: 140,
    enableSorting: true,
    Cell: ({ cell }: any) => {
      const val = cell.getValue();
      if (val === null || val === undefined || isNaN(Number(val))) return "-";
      return `$${Number(val).toLocaleString("en-US")}`;
    },
  },
  {
    accessorKey: "eighteenCharacterAccountId",
    header: "18 Character Account ID",
    minSize: 200,
    enableSorting: false,
  },
  {
    accessorKey: "eighteenCharOppId",
    header: "18 Char Opp ID",
    minSize: 200,
    enableSorting: false,
  },
];
